/* eslint-disable no-param-reassign */
import { distinctUntilChanged, tap, withLatestFrom } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  ScrollType,
  MutationObserverCustomConfig,
  DragStates,
  MutationObserverAttributesFilters,
  ScrollControlStates,
  ScrollHandlers,
  ScrollStates,
} from './content-scroll.types';
import { ScrollStateService } from '../scroll-state-service';

import { ScrollDirections } from '@app/shared/types/enums/scroll-directions.enum';
import { E2E_ATTRIBUTES } from '@app/shared/types/enums/e2e-attributes.enum';
import { BaseComponent } from '@app/components/base-component/base.component';
import { GridScrollModel } from '@app/components/dm-grid/dm-grid.type';
import { PlatformHelper } from '@app/shared/helpers/platform-helper/platform-helper';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'content-scroll',
  templateUrl: './content-scroll.component.html',
  styleUrls: [
    '../content-scroll-common.scss',
    './content-scroll.component.scss',
  ],
  standalone: false,
})
export class ContentScrollComponent
  extends BaseComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges
{
  // SCROLL_STEP is a part of scrollableBlock height that will be scrolled (1/9)
  public static SCROLL_STEP = 9;
  public scrollableBlock: HTMLElement;
  public title: string;
  public selectedElementId: string | null;
  public timeoutID: number;
  public isScrollActive = false;
  public isIOS = PlatformHelper.isIOS();
  public previousOrderItems: string[] | null;
  public E2E_ATTRIBUTES: typeof E2E_ATTRIBUTES = E2E_ATTRIBUTES;

  public state: ScrollStates = {
    stateId: '',
    isTouchDevice: false,
    isHorizontal: false,
    isVertical: false,
    isTop: false,
    isBottom: false,
    isLeft: false,
    isRight: false,
  };

  public controlStates: ScrollControlStates = {
    isHorizontal: false,
    isVertical: false,
    isTopBtnDisabled: false,
    isTopBtnFullContentDisabled: false,
    isBottomBtnDisabled: false,
    isBottomBtnFullContentDisabled: false,
    isLeftBtnDisabled: false,
    isLeftBtnFullContentDisabled: false,
    isRightBtnDisabled: false,
    isRightBtnFullContentDisabled: false,
    showTopBtn: true,
    showTopBtnFullContent: true,
    showBottomBtn: true,
    showBottomBtnFullContent: true,
    showRightBtn: true,
    showRightBtnFullContent: true,
    showLeftBtn: true,
    showLeftBtnFullContent: true,
    isTopFader: false,
    isBottomFader: false,
    isLeftFader: false,
    isRightFader: false,
    isVirtualHorizontalScrollShown: false,
    isVirtualVerticalScrollShown: false,
  };
  public dragStates: DragStates = {
    verticalDragHeight: 0,
    horizontalDragWidth: 0,
    dragTop: 0,
    dragLeft: 0,
    dragMaxY: 0,
    dragMaxX: 0,
  };

  // gridScroll inputs
  @Input() public gridScroll = false;
  @Input() public gridScrollConfig: GridScrollModel;
  @Input() public infiniteRowsAmount = false;
  @Input() public adjustHeightForSingleTouchScroll = false;
  @Input() public mutationObserverCustomConfig: MutationObserverCustomConfig;
  @Input() public hideFaders = false;
  @Input() public autoScrollToItem = false;
  @Input() public autoScrollToNewItem = false;
  @Input() public autoScrollToStart = false;
  @Input() public isDisplayVerticalScroll = false;
  @Input() public isDisplayHorizontalScroll = false;
  @Input() public parentName: string;

  // gridScroll outputs
  @Output() public changeGridVerticalScroll = new EventEmitter<{
    direction: ScrollDirections;
    position?: 'top' | 'bottom' | 'middle' | null;
  }>();

  @Output() public changeGridHorizontalScroll = new EventEmitter<{
    direction: ScrollDirections;
    step?: number;
  }>();
  @Output() public onScrolled = new EventEmitter<Record<string, number>>();
  @Output() public isHorizontalScroll = new EventEmitter<boolean>();
  @ViewChild('verticalVirtualScroll', { static: true })
  public verticalVirtualScrollRef: ElementRef;
  @ViewChild('horizontalVirtualScroll', { static: true })
  public horizontalVirtualScrollRef: ElementRef;

  private isBounced$ = new BehaviorSubject<boolean>(false);
  private domChange$: Subject<MutationRecord[]> = new Subject();

  private ATTR_FILTERS: MutationObserverAttributesFilters = {
    [ScrollType.Ticket]: 'scroll-id',
    [ScrollType.ItemScroll]: 'scroll-active',
  };

  private sizeContentBlock = {
    middle: 390,
    small: 270,
  };
  private HANDLERS: ScrollHandlers = {
    [ScrollType.ItemScroll]: (mutations: MutationRecord[] = []): void => {
      const activeItem = mutations.find(
        ({ target }: MutationRecord) =>
          (<Element>target).getAttribute(
            this.ATTR_FILTERS[ScrollType.ItemScroll],
          ) === 'true',
      );

      if (!activeItem) {
        return;
      }

      const SCROLL_CONTAINER_SHADOW_HEIGHT = 8;
      const elemOffsetTop = this.getOffsetTop(<HTMLElement>activeItem.target);
      const elemOffsetHeight = (<HTMLElement>activeItem.target).offsetHeight;
      const elemOffsetBottom = elemOffsetTop + elemOffsetHeight;
      const blockOffsetHeight = this.scrollableBlock.offsetHeight;

      const elemDOMRect: DOMRect = (<HTMLElement>(
        activeItem.target
      )).getBoundingClientRect();
      const blockDOMRect: DOMRect =
        this.scrollableBlock.getBoundingClientRect();
      const elemTooHigh = elemDOMRect.top < blockDOMRect.top;
      const elemTooLow = elemDOMRect.bottom > blockDOMRect.bottom;
      const elemNotFullyVisible = elemTooHigh || elemTooLow;

      if (!elemNotFullyVisible) {
        return;
      }

      let scrollToHere = elemOffsetTop - SCROLL_CONTAINER_SHADOW_HEIGHT * 2;

      if (elemTooLow) {
        scrollToHere =
          elemOffsetBottom -
          blockOffsetHeight +
          SCROLL_CONTAINER_SHADOW_HEIGHT * 2;
      }

      this.scrollToVerticalPosition(scrollToHere);
    },
  };

  private mutationObserver: MutationObserver;
  private mutationObserverConfig: MutationObserverInit = {};
  private resizeObserver: ResizeObserver;
  private resizeRafId: number = 0;
  private resizePrevDimensions = { width: 0, height: 0 };
  private resizeLastDimensions = { width: 0, height: 0 };

  private readonly VERTICAL_CONTROL_CLASS: string = 'has-vertical-control';
  private readonly HORIZONTAL_CONTROL_CLASS: string = 'has-horizontal-control';
  private readonly VERTICAL_INDICATOR_CLASS: string = 'has-vertical-indicator';
  private readonly HORIZONTAL_INDICATOR_CLASS: string =
    'has-horizontal-indicator';

  private readonly ADJUST_HOST_HEIGHT_CLASS: string = 'change-host-height';

  public constructor(
    protected element: ElementRef,
    protected changeDetectorRef: ChangeDetectorRef,
    protected scrollStateService: ScrollStateService,
    injector: Injector,
  ) {
    super(injector);
  }

  public ngOnInit(): void {
    this.mutationObserverConfig = this.createMutationObserverConfig(
      this.mutationObserverCustomConfig,
      this.ATTR_FILTERS,
    );
  }

  public ngAfterViewInit(): void {
    this.scrollableBlock =
      this.element.nativeElement.querySelector('.content-scrollbar');
    this.state.isTouchDevice = PlatformHelper.isTouchDevice();
    this.updateScrollView();

    this.trackSizeAndUpdateScrollView();

    this.initMutationsObserver();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.gridScrollConfig) {
      this.updateScrollView();
      this.changeDetectorRef.markForCheck();
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();

    window.clearTimeout(this.timeoutID);
    cancelAnimationFrame(this.resizeRafId);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (!this.mutationObserver) {
      return;
    }
    this.mutationObserver.disconnect();
  }

  /**
   * @method scrollToPosition
   * Scroll content vertically to the given position.
   */
  public scrollToVerticalPosition(position: number): void {
    this.scrollableBlock.scrollTop = position;

    this.scrollVirtualVertical();
  }

  /**
   * @method scrollToHorizontalPosition
   * @param position - amount of pixels should be scrolled
   */
  public scrollToHorizontalPosition(position: number): void {
    this.scrollableBlock.scrollLeft = position;

    this.scrollVirtualHorizontal();
  }

  public scrollGridVertically(
    direction: ScrollDirections,
    position?: 'top' | 'bottom' | 'middle' | null,
  ): void {
    if (this.gridScroll) {
      this.changeGridVerticalScroll.emit({ direction, position });
    }
  }

  public scrollGridHorizontally(
    direction: ScrollDirections,
    step?: number,
  ): void {
    if (this.gridScroll) {
      this.changeGridHorizontalScroll.emit({ direction, step });
    }
  }

  /**
   * @method scrollTo
   * Scroll to next screen.
   */
  public scrollScreenVertically(
    direction: ScrollDirections = ScrollDirections.BOTTOM,
  ): void {
    if (direction === ScrollDirections.TOP) {
      this.scrollableBlock.scrollTop -= this.scrollableBlock.clientHeight;
    } else {
      this.scrollableBlock.scrollTop += this.scrollableBlock.clientHeight;
    }
    this.scrollGridVertically(direction, 'middle');
    this.scrollVirtualVertical();
  }

  /**
   * @method scrollTo
   * Full content scroll.
   */
  public scrollFullContentVertically(
    direction: ScrollDirections = ScrollDirections.BOTTOM,
  ): void {
    if (direction === ScrollDirections.TOP) {
      this.scrollableBlock.scrollTop -= this.scrollableBlock.clientHeight;
    } else if (direction === ScrollDirections.BOTTOM) {
      this.scrollableBlock.scrollTop += this.scrollableBlock.clientHeight;
    } else if (direction === ScrollDirections.FULL_BOTTOM) {
      this.scrollableBlock.scrollTop += this.scrollableBlock.scrollHeight;
    } else if (direction === ScrollDirections.FULL_TOP) {
      this.scrollableBlock.scrollTop -= this.scrollableBlock.scrollHeight;
    }
    this.scrollGridVertically(direction);
    this.scrollVirtualVertical();
  }

  /**
   * @method stepTo
   * Step to next part of screen.
   */
  public scrollStepVertically(
    direction: ScrollDirections = ScrollDirections.BOTTOM,
  ): void {
    if (direction === ScrollDirections.TOP) {
      this.scrollableBlock.scrollTop -=
        this.scrollableBlock.clientHeight / ContentScrollComponent.SCROLL_STEP;
    } else {
      this.scrollableBlock.scrollTop +=
        this.scrollableBlock.clientHeight / ContentScrollComponent.SCROLL_STEP;
    }
    this.scrollGridVertically(direction, 'bottom');
    this.scrollVirtualVertical();
  }

  /**
   * @method scrollHorizontalTo
   * Scroll to next screen.
   */
  public scrollScreenHorizontally(
    direction: ScrollDirections = ScrollDirections.RIGHT,
  ): void {
    if (direction === ScrollDirections.LEFT) {
      this.scrollableBlock.scrollLeft -= this.scrollableBlock.clientWidth;
    } else {
      this.scrollableBlock.scrollLeft += this.scrollableBlock.clientWidth;
    }
    this.scrollGridHorizontally(direction, this.scrollableBlock.clientWidth);
    this.scrollVirtualHorizontal();
  }

  /**
   * @method stepHorizontalTo
   * Step to next part of screen.
   */
  public scrollStepHorizontally(
    direction: ScrollDirections = ScrollDirections.RIGHT,
  ): void {
    if (direction === ScrollDirections.LEFT) {
      this.scrollableBlock.scrollLeft -=
        this.scrollableBlock.clientWidth / ContentScrollComponent.SCROLL_STEP;
    } else {
      this.scrollableBlock.scrollLeft +=
        this.scrollableBlock.clientWidth / ContentScrollComponent.SCROLL_STEP;
    }
    this.scrollGridHorizontally(
      direction,
      this.scrollableBlock.clientWidth / ContentScrollComponent.SCROLL_STEP,
    );
    this.scrollVirtualHorizontal();
  }

  /**
   * @method stepHorizontalTo
   * Full content scroll.
   */
  public scrollFullContentHorizontally(
    direction: ScrollDirections = ScrollDirections.FULL_RIGHT,
  ): void {
    if (direction === ScrollDirections.FULL_LEFT) {
      this.scrollableBlock.scrollLeft -= this.scrollableBlock.scrollWidth;
    } else {
      this.scrollableBlock.scrollLeft += this.scrollableBlock.scrollWidth;
    }
    this.scrollGridHorizontally(direction, this.scrollableBlock.scrollWidth);
    this.scrollVirtualHorizontal();
  }

  /**
   * @method getNewItemId - sets id of a new element,
   * which was added to content scroll component
   *
   * @param {string} id - id of an added element to scroll container
   */
  public getNewItemId(id: string): void {
    this.selectedElementId = id;
  }

  /**
   * @method onScroll - updates controls state (active/disabled buttons, shadows on screen borders)
   * and emits current scroll coordinates
   */
  public onScroll(event: Event): void {
    this.isScrollActive = true;
    this.isBounced$.next(this.isBouncedScroll(event));
    this.updateScrollView();

    window.clearTimeout(this.timeoutID);

    this.timeoutID = window.setTimeout(() => {
      this.onScrollEnd();
    }, 100);

    this.scrollVirtualScrollbars();

    this.onScrolled.emit({
      top: this.scrollableBlock.scrollTop,
      left: this.scrollableBlock.scrollLeft,
    });
  }

  public scrollToStart(): void {
    if (this.scrollableBlock) {
      this.scrollToHorizontalPosition(0);
      this.scrollToVerticalPosition(0);
    }
  }

  private trackSizeAndUpdateScrollView(): void {
    // Tracks last two dimension snapshots to prevent ResizeObserver feedback loops.
    // updateScrollView() toggles CSS classes that change layout, which re-triggers ResizeObserver.
    // Comparing against both prev and last dimensions stops the oscillation cycle on the third call.
    this.resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || entry.contentRect.height <= 0) {
        return;
      }

      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);

      const matchesLast =
        width === this.resizeLastDimensions.width &&
        height === this.resizeLastDimensions.height;
      const matchesPrev =
        width === this.resizePrevDimensions.width &&
        height === this.resizePrevDimensions.height;

      if (matchesLast || matchesPrev) {
        return;
      }

      this.resizePrevDimensions = { ...this.resizeLastDimensions };
      this.resizeLastDimensions = { width, height };

      // Batch multiple resize events per frame into a single updateScrollView() call
      cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = requestAnimationFrame(() => this.updateScrollView());
    });
    this.resizeObserver.observe(this.scrollableBlock);
  }

  /**
   * @method updateScrollView - updates controls state (active/disabled buttons, shadows on screen borders)
   */
  private updateScrollView(): void {
    this.calculateState();
    this.calculateControlsState();
    this.calculateVirtualScrollbarsSize();
    this.stateChanged();
    // detect changes so that if the content within changes, the scroll elements will hide/show appropriately
    this.changeDetectorRef.detectChanges();
  }

  private stateChanged(): void {
    if (this.state.isHorizontal && !this.state.isTouchDevice) {
      this.isHorizontalScroll.emit(true);
    }
  }

  private scrollVirtualScrollbars(): void {
    if (this.controlStates.isVirtualVerticalScrollShown) {
      this.scrollVirtualVertical();
    }
    if (this.controlStates.isVirtualHorizontalScrollShown) {
      this.scrollVirtualHorizontal();
    }
  }

  private calculateVirtualScrollbarsSize(): void {
    if (this.controlStates.isVirtualVerticalScrollShown) {
      const trackHeight =
        this.verticalVirtualScrollRef.nativeElement.clientHeight;
      const scrollHeight = this.scrollableBlock.scrollHeight;
      const percentInViewV = scrollHeight / trackHeight;

      this.dragStates.verticalDragHeight = Math.ceil(
        (1 / percentInViewV) * trackHeight,
      );
      this.dragStates.dragMaxY =
        trackHeight - this.dragStates.verticalDragHeight;
    }

    if (this.controlStates.isVirtualHorizontalScrollShown) {
      const trackWidth =
        this.horizontalVirtualScrollRef.nativeElement.clientWidth;
      const scrollWidth = this.scrollableBlock.scrollWidth;
      const percentInViewH = scrollWidth / trackWidth;

      this.dragStates.horizontalDragWidth = Math.ceil(
        (1 / percentInViewH) * trackWidth,
      );
      this.dragStates.dragMaxX =
        trackWidth - this.dragStates.horizontalDragWidth;
    }
  }

  private scrollVirtualVertical(): void {
    const trackHeight =
      this.verticalVirtualScrollRef.nativeElement.clientHeight;
    const scrollHeight = this.scrollableBlock.scrollHeight;
    const scrollDestY = this.scrollableBlock.scrollTop;
    const percentScrolled = scrollDestY / (scrollHeight - trackHeight);

    this.positionDragY(percentScrolled * this.dragStates.dragMaxY);
  }

  private scrollVirtualHorizontal(): void {
    const trackWidth =
      this.horizontalVirtualScrollRef.nativeElement.clientWidth;
    const scrollWidth = this.scrollableBlock.scrollWidth;
    const scrollDestX = this.scrollableBlock.scrollLeft;
    const percentScrolled = scrollDestX / (scrollWidth - trackWidth);

    this.positionDragX(percentScrolled * this.dragStates.dragMaxX);
  }

  private positionDragY(destY: number): void {
    if (!this.controlStates.isVirtualVerticalScrollShown) {
      return;
    }

    if (destY < 0) {
      destY = 0;
    } else if (destY > this.dragStates.dragMaxY) {
      destY = this.dragStates.dragMaxY;
    }

    this.dragStates.dragTop = Math.floor(destY);
  }

  private positionDragX(destX: number): void {
    if (!this.controlStates.isVirtualHorizontalScrollShown) {
      return;
    }

    if (destX < 0) {
      destX = 0;
    } else if (destX > this.dragStates.dragMaxX) {
      destX = this.dragStates.dragMaxX;
    }

    this.dragStates.dragLeft = Math.floor(destX);
  }

  private onScrollEnd(): void {
    this.isScrollActive = false;
    this.changeDetectorRef.detectChanges();
  }

  private isBouncedScroll(event: Event): boolean {
    const target = <Element>event.target;
    return (
      this.isIOS &&
      (target.scrollTop < 0 ||
        target.scrollTop > target.scrollHeight - target.clientHeight)
    );
  }

  /**
   * @method setControlState.
   * Set template control states according to scroll states
   */
  private calculateControlsState(): void {
    this.controlStates.isTopBtnDisabled = this.state.isTop;
    this.controlStates.isTopBtnFullContentDisabled = this.state.isTop;
    this.controlStates.isBottomBtnDisabled = this.state.isBottom;
    this.controlStates.isBottomBtnFullContentDisabled = this.state.isBottom;
    this.controlStates.isLeftBtnDisabled = this.state.isLeft;
    this.controlStates.isLeftBtnFullContentDisabled = this.state.isLeft;
    this.controlStates.isRightBtnDisabled = this.state.isRight;
    this.controlStates.isRightBtnFullContentDisabled = this.state.isRight;

    this.controlStates.isTopFader =
      !this.hideFaders && this.state.isVertical && !this.state.isTop;
    this.controlStates.isBottomFader =
      !this.hideFaders && this.state.isVertical && !this.state.isBottom;
    this.controlStates.isLeftFader =
      !this.hideFaders && this.state.isHorizontal && !this.state.isLeft;
    this.controlStates.isRightFader =
      !this.hideFaders && this.state.isHorizontal && !this.state.isRight;

    this.controlStates.isVertical =
      this.state.isVertical && !this.state.isTouchDevice;
    this.controlStates.isHorizontal =
      this.state.isHorizontal && !this.state.isTouchDevice;

    this.controlStates.isVirtualVerticalScrollShown =
      this.state.isVertical && this.state.isTouchDevice;
    this.controlStates.isVirtualHorizontalScrollShown =
      this.state.isHorizontal && this.state.isTouchDevice;

    this.controlStates.showTopBtnFullContent =
      this.verticalVirtualScrollRef.nativeElement.clientHeight >=
        this.sizeContentBlock.middle && !this.infiniteRowsAmount;
    this.controlStates.showBottomBtnFullContent =
      this.verticalVirtualScrollRef.nativeElement.clientHeight >=
        this.sizeContentBlock.middle && !this.infiniteRowsAmount;
    this.controlStates.showTopBtn =
      this.verticalVirtualScrollRef.nativeElement.clientHeight >=
      this.sizeContentBlock.small;
    this.controlStates.showBottomBtn =
      this.verticalVirtualScrollRef.nativeElement.clientHeight >=
      this.sizeContentBlock.small;

    this.controlStates.showRightBtnFullContent =
      this.horizontalVirtualScrollRef.nativeElement.clientWidth >=
      this.sizeContentBlock.middle;
    this.controlStates.showLeftBtnFullContent =
      this.horizontalVirtualScrollRef.nativeElement.clientWidth >=
      this.sizeContentBlock.middle;
    this.controlStates.showRightBtn =
      this.horizontalVirtualScrollRef.nativeElement.clientWidth >=
      this.sizeContentBlock.small;
    this.controlStates.showLeftBtn =
      this.horizontalVirtualScrollRef.nativeElement.clientWidth >=
      this.sizeContentBlock.small;
    // Set class into template
    this.element.nativeElement.classList.toggle(
      this.VERTICAL_CONTROL_CLASS,
      this.controlStates.isVertical,
    );
    this.element.nativeElement.classList.toggle(
      this.HORIZONTAL_CONTROL_CLASS,
      this.controlStates.isHorizontal,
    );
    this.element.nativeElement.classList.toggle(
      this.VERTICAL_INDICATOR_CLASS,
      this.controlStates.isVirtualVerticalScrollShown,
    );
    this.element.nativeElement.classList.toggle(
      this.HORIZONTAL_INDICATOR_CLASS,
      this.controlStates.isVirtualHorizontalScrollShown,
    );
    this.element.nativeElement.classList.toggle(
      this.ADJUST_HOST_HEIGHT_CLASS,
      this.controlStates.isHorizontal && this.adjustHeightForSingleTouchScroll,
    );
  }

  /**
   * @method calculateState - calculates state which will be used in calculations
   * of controls state (active/disabled buttons, shadows on screen borders)
   */
  private calculateState(): void {
    this.state.isVertical = this.gridScroll
      ? this.gridScrollConfig.isTopFader || this.gridScrollConfig.isBottomFader
      : this.scrollableBlock.clientHeight < this.scrollableBlock.scrollHeight;
    this.state.isHorizontal = this.gridScroll
      ? this.gridScrollConfig.isLeftFader || this.gridScrollConfig.isRightFader
      : this.scrollableBlock.clientWidth < this.scrollableBlock.scrollWidth;

    this.state.isTop = this.gridScroll
      ? !this.gridScrollConfig.isTopFader
      : !this.scrollableBlock.scrollTop;
    this.state.isBottom = this.gridScroll
      ? !this.gridScrollConfig.isBottomFader
      : !(
          Math.ceil(this.scrollableBlock.scrollTop) <
          this.scrollableBlock.scrollHeight - this.scrollableBlock.clientHeight
        );

    this.state.isLeft = this.gridScroll
      ? !this.gridScrollConfig.isLeftFader
      : !this.scrollableBlock.scrollLeft;
    this.state.isRight = this.gridScroll
      ? !this.gridScrollConfig.isRightFader
      : !(
          Math.ceil(this.scrollableBlock.scrollLeft) <
          this.scrollableBlock.scrollWidth - this.scrollableBlock.clientWidth
        );

    this.state.stateId = this.parentName;
    if (this.state.stateId) {
      this.scrollStateService.updateScrollState(this.state);
    }
  }

  /**
   * @method initMutationsObserver - init MutationObserver which looks after
   * DOM changes, and if it's changed and option `autoScrollToNewItem` is set,
   * than scrolls to this new item
   */
  private initMutationsObserver(): void {
    this.mutationObserver = new MutationObserver(
      (mutations: MutationRecord[]) => {
        this.domChange$.next(mutations);
      },
    );
    this.mutationObserver.observe(
      this.scrollableBlock,
      this.mutationObserverConfig,
    );
    this.domChange$
      .pipe(
        withLatestFrom(this.isBounced$.pipe(distinctUntilChanged())),
        tap(([mutations, isBounced]) => {
          if (isBounced) {
            setTimeout(() => {
              this.reactOnDomChanges(mutations);
            }, 400);
          } else {
            this.reactOnDomChanges(mutations);
          }
        }),
      )
      .subscribe();
  }

  private reactOnDomChanges(mutations: MutationRecord[]): void {
    this.updateScrollView();

    if (this.autoScrollToStart) {
      this.scrollToStart();
      return;
    }

    if (this.autoScrollToItem) {
      this.HANDLERS[ScrollType.ItemScroll].call(this, mutations);
    }

    if (this.autoScrollToNewItem) {
      this.HANDLERS[ScrollType.Ticket].call(this, mutations);
    }
  }

  private getLastChild(targetEl: HTMLElement): HTMLElement | null {
    const children = targetEl && targetEl.children;
    return children && <HTMLElement>children.item(children.length - 1);
  }

  private getOffsetTop(element: HTMLElement): number {
    return element && element.offsetTop;
  }

  private createMutationObserverConfig(
    configKeys: MutationObserverCustomConfig,
    filters: MutationObserverAttributesFilters,
  ): MutationObserverInit {
    if (!configKeys || !Array.isArray(configKeys)) {
      return {
        childList: true,
        characterData: true,
        subtree: true,
      };
    }

    const config: MutationObserverInit = {
      attributes: false,
      characterData: false,
      childList: false,
      subtree: false,
    };

    configKeys.forEach((key) => (config[key] = true));

    if (config.attributes) {
      config.attributeFilter = Object.values(filters);
    }

    return config;
  }
}
